"use client";

import { useState } from "react";
import {
  type CipherMode,
  CipherWorkbench,
} from "@/components/tool-kit/cipher-workbench";
import {
  Checkbox,
  Notice,
  PillGroup,
  TextField,
} from "@/components/tool-kit/primitives";
import { HINT } from "@/components/tool-kit/styles";
import { KeyedWorking } from "@/components/tools/ciphers-polygraphic/keyed-working";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { tPlural } from "@/lib/i18n/interpolate";
import {
  ALPHABET,
  foreignLetters,
  KEEP_ALL_TEXT,
  letterIndex,
} from "@/lib/tool-kit/ciphers/alphabet";
import {
  type BeaufortKind,
  beaufortSquare,
  beaufortTransform,
} from "@/lib/tool-kit/ciphers-polygraphic/beaufort";
import {
  describeCalc,
  formatCipherOutput,
} from "@/lib/tool-kit/ciphers-polygraphic/keyed-letters";

const STEP_LIMIT = 20;
const LETTERS = [...ALPHABET];

/** English strings. A translated content file overrides these through `ui`. */
export const BEAUFORT_CIPHER_STRINGS = {
  mode: "Mode",
  encode: "Encode",
  decode: "Decode",
  plainText: "Plain text",
  cipherText: "Cipher text",
  decodedText: "Decoded text",
  inputPlaceholder: "Type or paste your message",
  sample: "Defend the east wall of the castle",
  keyword: "Keyword",
  keywordSample: "FORT",
  keywordHint:
    "Letters only. The keyword repeats along the message, one key letter for each letter of text.",
  keywordMissing: "Enter a keyword with at least one letter to see the result.",
  version: "Version",
  classic: "Beaufort — key minus letter",
  variant: "Variant Beaufort — letter minus key",
  versionHint:
    "The classic Beaufort is reciprocal: the same steps encode and decode. The variant is the Vigenère cipher run backwards, so decoding adds the key back.",
  group: "Cipher text in groups of five (letters only, in capitals)",
  keep: "Keep in the result",
  keepCase: "Lower case",
  keepSpaces: "Spaces",
  keepPunctuation: "Punctuation and numbers",
  foreign:
    "one: {count} letter outside A–Z was left unchanged: {letters}.|other: {count} letters outside A–Z were left unchanged: {letters}.",
  copy: "Copy result",
  copied: "Copied!",
  clear: "Clear",
  swap: "Swap input and output",
  outputPlaceholder: "The result appears here.",
  working: "Working",
  numbering:
    "Letters are numbered A = 0, B = 1 … Z = 25, and every sum wraps around at 26.",
  ruleClassicEncode: "Encoding: cipher letter = key letter − plain letter.",
  ruleClassicDecode:
    "Decoding: plain letter = key letter − cipher letter — the same subtraction, which is why the cipher is reciprocal.",
  ruleVariantEncode: "Encoding: cipher letter = plain letter − key letter.",
  ruleVariantDecode: "Decoding: plain letter = cipher letter + key letter.",
  squareLabel: "Beaufort square",
  squareLabelVariant: "Variant Beaufort square",
  squareHint:
    "Row = key letter, column = plain letter, cell = cipher letter. Select a letter below to light it up: its row and column turn blue and the cell where they meet turns amber.",
  plainLetter: "Plain letter",
  cipherLetter: "Cipher letter",
  keyLetter: "Key letter",
  arithmetic: "Arithmetic",
  showLetter: "Show {letter} in the square",
  stepLimit:
    "one: Showing the first letter.|other: Showing the first {count} letters.",
} as const;

export default function BeaufortCipherWidget() {
  const strings = useToolUi(BEAUFORT_CIPHER_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [mode, setMode] = useState<CipherMode>("encode");
  const [input, setInput] = useState<string>(strings.sample);
  const [keyword, setKeyword] = useState<string>(strings.keywordSample);
  const [kind, setKind] = useState<BeaufortKind>("beaufort");
  const [options, setOptions] = useState(KEEP_ALL_TEXT);
  const [group, setGroup] = useState(false);

  const result = beaufortTransform(input, keyword, kind, mode);
  const output = formatCipherOutput(
    result.output,
    options,
    group && mode === "encode"
  );
  const foreign = foreignLetters(input);
  const shown = result.steps.slice(0, STEP_LIMIT);
  const classic = kind === "beaufort";
  const encoding = mode === "encode";

  let rule: string = strings.ruleVariantDecode;
  if (classic) {
    rule = encoding ? strings.ruleClassicEncode : strings.ruleClassicDecode;
  } else if (encoding) {
    rule = strings.ruleVariantEncode;
  }

  const keyControls = (
    <div className="space-y-4">
      <TextField
        hint={strings.keywordHint}
        id="bf-keyword"
        label={strings.keyword}
        mono
        onChange={setKeyword}
        value={keyword}
      />
      <PillGroup
        label={strings.version}
        name="bf-version"
        onChange={setKind}
        options={[
          { value: "beaufort", label: strings.classic },
          { value: "variant", label: strings.variant },
        ]}
        value={kind}
      />
      <p className={HINT}>{strings.versionHint}</p>
      {encoding ? (
        <Checkbox checked={group} label={strings.group} onChange={setGroup} />
      ) : null}
    </div>
  );

  const working =
    result.key === "" ? (
      <Notice>{strings.keywordMissing}</Notice>
    ) : (
      <KeyedWorking
        head={[
          encoding ? strings.plainLetter : strings.cipherLetter,
          strings.keyLetter,
          strings.arithmetic,
          encoding ? strings.cipherLetter : strings.plainLetter,
        ]}
        limitNote={
          result.steps.length > STEP_LIMIT
            ? tPlural(strings.stepLimit, locale, STEP_LIMIT, {
                count: fmt(STEP_LIMIT),
              })
            : undefined
        }
        marks={shown.map((step) => ({
          row: letterIndex(step.key),
          column: letterIndex(step.plain),
        }))}
        rows={shown.map((step) => [
          encoding ? step.plain : step.cipher,
          step.key,
          describeCalc(step.calc),
          encoding ? step.cipher : step.plain,
        ])}
        rules={[strings.numbering, rule]}
        selectLabel={strings.showLetter}
        square={{
          cells: beaufortSquare(kind),
          columns: LETTERS.length,
          label: classic ? strings.squareLabel : strings.squareLabelVariant,
          hint: strings.squareHint,
          rowHeaders: LETTERS,
          columnHeaders: LETTERS,
        }}
      />
    );

  return (
    <CipherWorkbench
      idPrefix="bf"
      input={input}
      keyControls={keyControls}
      labels={{
        mode: strings.mode,
        encode: strings.encode,
        decode: strings.decode,
        input: encoding ? strings.plainText : strings.cipherText,
        inputPlaceholder: strings.inputPlaceholder,
        output: encoding ? strings.cipherText : strings.decodedText,
        outputPlaceholder: strings.outputPlaceholder,
        textOptions: strings.keep,
        keepCase: strings.keepCase,
        keepSpaces: strings.keepSpaces,
        keepPunctuation: strings.keepPunctuation,
        copy: strings.copy,
        copied: strings.copied,
        clear: strings.clear,
        swap: strings.swap,
        working: strings.working,
      }}
      mode={mode}
      notices={
        foreign.count > 0 ? (
          <Notice>
            {tPlural(strings.foreign, locale, foreign.count, {
              count: fmt(foreign.count),
              letters: foreign.letters.join(" "),
            })}
          </Notice>
        ) : null
      }
      onInputChange={setInput}
      onModeChange={setMode}
      output={output}
      textOptions={
        group && encoding
          ? undefined
          : {
              value: options,
              onChange: setOptions,
              show: ["keepCase", "keepSpaces", "keepPunctuation"],
            }
      }
      working={working}
    />
  );
}
